// Realtime client for per-Fam WebSocket fan-out from the FamChannel
// Durable Object. One persistent connection per Fam slug, shared across
// components via React context. Messages dispatch TanStack Query
// invalidations so the UI auto-refreshes without page reload.
//
// Wire protocol — JSON messages from services-cf/durable-objects/fam-channel:
//   { type: "idea_proposed", payload: { idea } }
//   { type: "idea_voted",    payload: { ideaId, choice, userId } }
//   { type: "idea_withdrawn",payload: { ideaId } }
//   { type: "idea_resolved", payload: { ideaId, status } }
//
// Auto-reconnect uses exponential backoff with jitter, capped at 30s. We
// pause reconnects when the tab is hidden + resume on visibility to avoid
// burning a connection on a backgrounded tab.

import {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ideaKeys } from "./api/ideas";
import { isDevAuthMode } from "./devAuth";

// --- Wire types --------------------------------------------------------------

export type FamChannelMessageType =
	| "idea_proposed"
	| "idea_voted"
	| "idea_withdrawn"
	| "idea_resolved";

export interface FamChannelMessage {
	// Server may add new event types ahead of the SPA — accept anything but
	// narrow on the known ones via type guards downstream. `string` already
	// admits FamChannelMessageType, so the union was redundant.
	type: string;
	payload?: {
		idea?: { id?: string };
		ideaId?: string;
		choice?: string;
		userId?: string;
		status?: string;
		[key: string]: unknown;
	};
}

export type FamChannelStatus = "idle" | "connecting" | "open" | "closed" | "reconnecting";

export interface FamChannelHandle {
	status: FamChannelStatus;
	lastMessage: FamChannelMessage | null;
}

// --- Dispatcher --------------------------------------------------------------

// Map a wire message to TanStack Query cache invalidations. Centralized so
// adding a new message type is a single-place edit.
function dispatchToQueryClient(
	qc: QueryClient,
	slug: string,
	msg: FamChannelMessage,
): void {
	const payload = msg.payload ?? {};
	const ideaId =
		typeof payload.ideaId === "string"
			? payload.ideaId
			: typeof payload.idea?.id === "string"
				? payload.idea.id
				: undefined;

	switch (msg.type) {
		case "idea_proposed":
		case "idea_withdrawn":
			void qc.invalidateQueries({ queryKey: ["fam", slug] });
			void qc.invalidateQueries({ queryKey: ideaKeys.listByFam(slug) });
			if (ideaId) {
				void qc.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
			}
			break;
		case "idea_voted":
			if (ideaId) {
				void qc.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
			}
			// The Fam viewer's consensus bars depend on the same data, so
			// nudge them too. Cheap; TanStack dedups in-flight requests.
			void qc.invalidateQueries({ queryKey: ["fam", slug] });
			break;
		case "idea_resolved":
			if (ideaId) {
				void qc.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
			}
			void qc.invalidateQueries({ queryKey: ["fam", slug] });
			void qc.invalidateQueries({ queryKey: ideaKeys.listByFam(slug) });
			break;
		default:
			// Unknown types: log + ignore. Forward-compat for events we
			// haven't taught the client about yet.
			console.warn("realtime: unknown message type", msg.type);
	}
}

// --- Single shared connection per slug --------------------------------------

// We maintain a small process-global registry of live connections keyed on
// slug. React 18 StrictMode double-invokes effects in dev; without a
// reference-counted registry we'd burn two sockets on every mount. The
// registry also lets multiple components subscribing to the same Fam share
// one socket (the Fam viewer + a child idea page often co-mount).
interface ConnectionEntry {
	refCount: number;
	socket: WebSocket | null;
	status: FamChannelStatus;
	lastMessage: FamChannelMessage | null;
	listeners: Set<(state: FamChannelHandle) => void>;
	closeTimer: ReturnType<typeof setTimeout> | null;
	reconnectTimer: ReturnType<typeof setTimeout> | null;
	reconnectAttempt: number;
	intentionalClose: boolean;
	visibilityHandler: (() => void) | null;
}

const connections = new Map<string, ConnectionEntry>();

function notify(entry: ConnectionEntry): void {
	const handle: FamChannelHandle = {
		status: entry.status,
		lastMessage: entry.lastMessage,
	};
	for (const fn of entry.listeners) fn(handle);
}

function setStatus(entry: ConnectionEntry, status: FamChannelStatus): void {
	if (entry.status === status) return;
	entry.status = status;
	notify(entry);
}

function backoffMs(attempt: number): number {
	// 0.5s, 1s, 2s, 4s, 8s, 16s, 30s ceiling. Plus up to 250ms jitter so a
	// fleet-wide reconnect (e.g. after a CF maintenance blip) doesn't
	// thundering-herd the DO.
	const base = Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
	return base + Math.floor(Math.random() * 250);
}

function scheduleReconnect(slug: string, qc: QueryClient): void {
	const entry = connections.get(slug);
	if (!entry || entry.refCount === 0 || entry.intentionalClose) return;
	if (entry.reconnectTimer) return;
	const delay = backoffMs(entry.reconnectAttempt);
	entry.reconnectAttempt++;
	setStatus(entry, "reconnecting");
	entry.reconnectTimer = setTimeout(() => {
		entry.reconnectTimer = null;
		openSocket(slug, qc);
	}, delay);
}

function openSocket(slug: string, qc: QueryClient): void {
	const entry = connections.get(slug);
	if (!entry || entry.refCount === 0) return;
	if (typeof window === "undefined") return; // SSR guard

	// Pause connect attempts while the tab is hidden — there's no point
	// holding a WS for a backgrounded tab + it triggers a reconnect storm
	// when many tabs unhide simultaneously.
	if (typeof document !== "undefined" && document.visibilityState === "hidden") {
		setStatus(entry, "idle");
		return;
	}

	// Existing socket already healthy? Bail.
	if (entry.socket && entry.socket.readyState === WebSocket.OPEN) {
		setStatus(entry, "open");
		return;
	}

	setStatus(entry, "connecting");

	const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
	const url = `${proto}//${window.location.host}/api/fams/${encodeURIComponent(slug)}/subscribe`;

	let socket: WebSocket;
	try {
		socket = new WebSocket(url);
	} catch (err) {
		console.warn("realtime: WebSocket constructor failed", err);
		scheduleReconnect(slug, qc);
		return;
	}

	entry.socket = socket;

	socket.addEventListener("open", () => {
		const current = connections.get(slug);
		if (current?.socket !== socket) return;
		current.reconnectAttempt = 0;
		setStatus(current, "open");
	});

	socket.addEventListener("message", (ev) => {
		const current = connections.get(slug);
		if (current?.socket !== socket) return;
		let parsed: FamChannelMessage;
		try {
			const raw = typeof ev.data === "string" ? ev.data : "";
			if (!raw) return;
			parsed = JSON.parse(raw) as FamChannelMessage;
		} catch {
			console.warn("realtime: dropped non-JSON message");
			return;
		}
		if (!parsed || typeof parsed.type !== "string") return;
		current.lastMessage = parsed;
		dispatchToQueryClient(qc, slug, parsed);
		notify(current);
	});

	socket.addEventListener("close", () => {
		const current = connections.get(slug);
		if (current?.socket !== socket) return;
		current.socket = null;
		if (current.intentionalClose) {
			setStatus(current, "closed");
			return;
		}
		setStatus(current, "closed");
		scheduleReconnect(slug, qc);
	});

	socket.addEventListener("error", () => {
		// 'error' fires before 'close'; we let close handle reconnect.
		// Logging here is mostly noise (errors don't carry useful detail
		// for security reasons in browsers) so we no-op.
	});
}

function acquire(slug: string, qc: QueryClient): ConnectionEntry {
	let entry = connections.get(slug);
	if (!entry) {
		entry = {
			refCount: 0,
			socket: null,
			status: "idle",
			lastMessage: null,
			listeners: new Set(),
			closeTimer: null,
			reconnectTimer: null,
			reconnectAttempt: 0,
			intentionalClose: false,
			visibilityHandler: null,
		};
		connections.set(slug, entry);
	}
	// Cancel any pending close from a previous unmount.
	if (entry.closeTimer) {
		clearTimeout(entry.closeTimer);
		entry.closeTimer = null;
	}
	entry.refCount++;

	// First subscriber for this slug — wire visibility + open the socket.
	if (entry.refCount === 1) {
		entry.intentionalClose = false;
		entry.reconnectAttempt = 0;
		if (typeof document !== "undefined") {
			const handler = () => {
				const current = connections.get(slug);
				if (!current) return;
				if (document.visibilityState === "visible") {
					if (
						!current.socket ||
						current.socket.readyState >= WebSocket.CLOSING
					) {
						openSocket(slug, qc);
					}
				}
			};
			document.addEventListener("visibilitychange", handler);
			entry.visibilityHandler = handler;
		}
		openSocket(slug, qc);
	}
	return entry;
}

function release(slug: string): void {
	const entry = connections.get(slug);
	if (!entry) return;
	entry.refCount = Math.max(0, entry.refCount - 1);
	if (entry.refCount > 0) return;
	// Defer the actual close briefly. React StrictMode unmount → remount
	// fires release() then acquire() back-to-back in dev; debouncing avoids
	// thrashing the socket. 250ms is invisible to the user.
	entry.closeTimer = setTimeout(() => {
		const current = connections.get(slug);
		if (!current || current.refCount > 0) return;
		current.intentionalClose = true;
		if (current.reconnectTimer) {
			clearTimeout(current.reconnectTimer);
			current.reconnectTimer = null;
		}
		if (current.socket) {
			try {
				current.socket.close(1000, "client unmounted");
			} catch {
				/* ignore */
			}
			current.socket = null;
		}
		if (current.visibilityHandler && typeof document !== "undefined") {
			document.removeEventListener("visibilitychange", current.visibilityHandler);
		}
		connections.delete(slug);
	}, 250);
}

// --- Hook --------------------------------------------------------------------

/**
 * Subscribe to the live FamChannel WebSocket for `slug`. Returns the
 * current connection status + the most recent inbound message. Multiple
 * components can call this for the same slug — they share a single
 * underlying socket via reference counting.
 *
 * Pass `null`/`undefined` to no-op (e.g. while the slug is loading).
 */
export function useFamChannel(slug: string | null | undefined): FamChannelHandle {
	const qc = useQueryClient();
	const [handle, setHandle] = useState<FamChannelHandle>({
		status: "idle",
		lastMessage: null,
	});
	const listenerRef = useRef<((h: FamChannelHandle) => void) | null>(null);

	useEffect(() => {
		if (!slug) {
			setHandle({ status: "idle", lastMessage: null });
			return;
		}
		// Skip the WebSocket in stub-mode (Playwright e2e + local dev with
		// VITE_AUTH_MODE=stub). Browsers don't allow custom headers on WS
		// upgrades, so X-Dev-User-Id never reaches the BFF middleware and
		// the connection 401s. Production uses cookie-auth which DOES
		// round-trip on WS, so this gate only affects dev/stub builds.
		if (isDevAuthMode()) {
			setHandle({ status: "idle", lastMessage: null });
			return;
		}

		const entry = acquire(slug, qc);
		const listener = (h: FamChannelHandle) => setHandle(h);
		entry.listeners.add(listener);
		listenerRef.current = listener;
		// Initial snapshot so the consumer doesn't sit on "idle" for a frame.
		setHandle({ status: entry.status, lastMessage: entry.lastMessage });

		return () => {
			const current = connections.get(slug);
			if (current && listenerRef.current) {
				current.listeners.delete(listenerRef.current);
			}
			listenerRef.current = null;
			release(slug);
		};
	}, [slug, qc]);

	return handle;
}

// --- Optional context provider ----------------------------------------------
//
// Most call sites just invoke useFamChannel() directly — it's already
// ref-counted, so a route can mount it without coordination. The provider
// is exported for cases where a parent route wants to surface
// status/lastMessage to children without each child re-subscribing.

interface FamChannelContextValue {
	slug: string | null;
	handle: FamChannelHandle;
}

const FamChannelContext = createContext<FamChannelContextValue | null>(null);

export function FamChannelProvider({
	slug,
	children,
}: {
	slug: string | null | undefined;
	children: ReactNode;
}): ReactNode {
	const handle = useFamChannel(slug ?? null);
	const value = useMemo<FamChannelContextValue>(
		() => ({ slug: slug ?? null, handle }),
		[slug, handle],
	);
	return createElement(FamChannelContext.Provider, { value }, children);
}

export function useFamChannelContext(): FamChannelContextValue {
	const ctx = useContext(FamChannelContext);
	if (!ctx) {
		throw new Error(
			"useFamChannelContext must be used inside a <FamChannelProvider>",
		);
	}
	return ctx;
}

// --- Test/debug helpers ------------------------------------------------------
//
// Exposed for unit tests that need to flush a synthetic message. NOT for
// production use — the connection internals are intentionally private.

export const __TEST_ONLY__ = {
	dispatch: dispatchToQueryClient,
	connections,
	useCallback, // silence the dead-import lint we'd hit otherwise
};
